import { NavLink, useNavigate } from 'react-router-dom'
import {
  ClipboardList,
  ClipboardCheck,
  FileSpreadsheet,
  History,
  LayoutDashboard,
  LogOut,
  Package,
  Tag,
  Users,
  UserCog,
  GitBranch,
  Database,
  X,
  ChevronRight,
  User,
  type LucideIcon,
} from 'lucide-react'
import leapFaviconWhite from '@/assets/leap-favicon-white.png'
import { RegionSwitcher } from '@/components/common/RegionSwitcher'
import { LEAP_RED } from '@/constants/brand'
import { useRfidDrawer } from '@/contexts/RfidDrawerContext'
import { useAuthStore } from '@/stores/authStore'
import { useRegionStore } from '@/stores/regionStore'
import { canAccessCustoutValidation } from '@/config/regions'
import { displayUserName, cn } from '@/utils/cn'
import { ThemeSwitcher } from '@/components/ui/ThemeSwitcher'
import { canAccessRfidEngineAdmin } from '@/utils/rfidAccess'

const APP_NAME = import.meta.env.VITE_APP_NAME || 'RFID Track & Trace'

type MobileTool = {
  to: string
  icon: LucideIcon
  title: string
  subtitle: string
  indiaOnly?: boolean
}

const TOOLS: MobileTool[] = [
  {
    to: '/movement-document',
    icon: ClipboardList,
    title: 'Movement Document',
    subtitle: 'Look up a document journey',
  },
  {
    to: '/tag-journey',
    icon: Tag,
    title: 'Tag Journey',
    subtitle: 'Follow a single RFID tag',
  },
  {
    to: '/party-journey',
    icon: Users,
    title: 'Party Journey',
    subtitle: 'Sender / receiver history',
  },
  {
    to: '/current-state',
    icon: Package,
    title: 'Current State',
    subtitle: 'Latest known tag locations',
  },
  {
    to: '/movements',
    icon: History,
    title: 'Movements',
    subtitle: 'Browse movement history',
  },
  {
    to: '/reports',
    icon: FileSpreadsheet,
    title: 'RFID Reports',
    subtitle: 'Filter and export reports',
  },
  {
    to: '/validation-report',
    icon: ClipboardCheck,
    title: 'RFID Validation Report',
    subtitle: 'Draft vs SAP CUSTOUT tags',
    indiaOnly: true,
  },
  {
    to: '/c2-c1-mapping',
    icon: GitBranch,
    title: 'Asset Life Cycle',
    subtitle: 'Partner and plant path visualiser',
  },
  {
    to: '/admin/draft-users',
    icon: UserCog,
    title: 'Draft Users',
    subtitle: 'CUSTOUT draft allowlist',
  },
]

const ENGINE_TOOL: MobileTool = {
  to: '/admin/rfid-tags',
  icon: Database,
  title: 'RFID Tags',
  subtitle: 'Master list & mapping report',
}

export function RfidMobileSidebar() {
  const { open, closeDrawer } = useRfidDrawer()
  const { user, logout } = useAuthStore()
  const navigate = useNavigate()
  const regionView = useRegionStore((s) => s.view)
  const availableRegions = useRegionStore((s) => s.availableRegions)
  const showValidation = canAccessCustoutValidation(regionView, availableRegions)
  const tools = TOOLS.filter((item) => !item.indiaOnly || showValidation)
  if (canAccessRfidEngineAdmin(user)) tools.push(ENGINE_TOOL)

  if (!open) return null

  const displayName = displayUserName(user) || 'Signed in'
  const secondary =
    user?.email && user?.name ? user.email : user?.userId ? String(user.userId) : ''

  const go = (path: string) => {
    closeDrawer()
    navigate(path)
  }

  const onLogout = async () => {
    closeDrawer()
    await logout()
    navigate('/login', { replace: true })
  }

  return (
    <div className="fixed inset-0 z-[60] lg:hidden" role="dialog" aria-modal="true">
      <button
        type="button"
        className="absolute inset-0 bg-black/45"
        aria-label="Close menu"
        onClick={closeDrawer}
      />

      <aside
        className="absolute inset-y-0 left-0 flex w-[min(82vw,320px)] flex-col border-r border-border bg-card text-foreground shadow-2xl"
        style={{ animation: 'rfidDrawerInLtr 280ms cubic-bezier(0.22,1,0.36,1)' }}
      >
        <div className="flex items-center gap-3 border-b border-border px-4 py-3.5 pt-[max(0.875rem,env(safe-area-inset-top))]">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px]"
            style={{ background: LEAP_RED }}
          >
            <img
              src={leapFaviconWhite}
              alt=""
              className="h-[22px] w-[22px] rounded object-contain"
              draggable={false}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-black text-foreground">{APP_NAME}</div>
            <div className="truncate text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Movement viz
            </div>
          </div>
          <button
            type="button"
            onClick={closeDrawer}
            className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 space-y-5 overflow-y-auto px-3 py-4">
          <section>
            <div className="rfid-section-label mb-2 px-1">Profile</div>
            <button
              type="button"
              onClick={() => go('/dashboard')}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-muted/40 px-3 py-3 text-left"
            >
              <span
                className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-white"
                style={{ background: LEAP_RED }}
              >
                <User className="h-[18px] w-[18px]" strokeWidth={2} />
              </span>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-extrabold text-foreground">
                  {displayName}
                </span>
                {secondary ? (
                  <span className="mt-0.5 block truncate text-xs font-semibold text-muted-foreground">
                    {secondary}
                  </span>
                ) : null}
              </span>
              <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </section>

          <section>
            <button
              type="button"
              onClick={() => go('/dashboard')}
              className="flex w-full items-center gap-3 rounded-xl px-3 py-3.5 text-left text-white shadow-sm"
              style={{ background: LEAP_RED }}
            >
              <LayoutDashboard className="h-[22px] w-[22px] shrink-0" strokeWidth={2.4} />
              <span className="min-w-0 flex-1">
                <span className="block text-[15px] font-black">Overview</span>
                <span className="mt-0.5 block text-[11px] font-semibold text-white/80">
                  Live Track & Trace summary
                </span>
              </span>
            </button>
          </section>

          <section>
            <div className="rfid-section-label mb-2 px-1">Region</div>
            <div className="rounded-xl border border-border bg-card px-3 py-2.5">
              <RegionSwitcher />
            </div>
          </section>

          <section>
            <div className="rfid-section-label mb-2 px-1">Tools</div>
            <div className="space-y-1.5">
              {tools.map(({ to, icon: Icon, title, subtitle }) => (
                <NavLink
                  key={to}
                  to={to}
                  onClick={closeDrawer}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-xl border px-3 py-2.5 transition-colors',
                      isActive
                        ? 'border-[#AF0000]/40 bg-[#FDECEA]'
                        : 'border-border bg-card hover:bg-muted/50'
                    )
                  }
                >
                  <Icon
                    className="h-[18px] w-[18px] shrink-0"
                    style={{ color: LEAP_RED }}
                    strokeWidth={2}
                  />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-extrabold text-foreground">{title}</span>
                    {subtitle ? (
                      <span className="mt-0.5 block text-[11px] font-semibold text-muted-foreground">
                        {subtitle}
                      </span>
                    ) : null}
                  </span>
                </NavLink>
              ))}
            </div>
          </section>

          <section className="space-y-2">
            <div className="rfid-section-label mb-2 px-1">Options</div>
            <ThemeSwitcher variant="surface" className="w-full" />
            <button
              type="button"
              onClick={() => void onLogout()}
              className="flex w-full items-center gap-3 rounded-xl border border-border bg-card px-3 py-2.5 text-sm font-extrabold text-foreground hover:bg-muted/50"
            >
              <LogOut className="h-[18px] w-[18px]" style={{ color: LEAP_RED }} strokeWidth={2} />
              Sign out
            </button>
          </section>
        </div>

        <div className="border-t border-border px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] text-center">
          <div className="text-[11px] font-semibold text-muted-foreground">
            LEAP India · RFID Track & Trace
          </div>
        </div>
      </aside>
    </div>
  )
}
